import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../components/ui';
import { Button, Input, Spinner, Badge } from '../../components/ui';
import {
  CheckCircle, Smartphone, Building, AlertCircle, ArrowRight,
  Globe, Zap, Sparkles, MessageSquare, Send, Clock, RefreshCw, Check
} from 'lucide-react';
import './Onboarding.css';

const VIEW = {
  LOADING: 'loading',
  FORM: 'form',
  AWAITING: 'awaiting',
  ACTIVE: 'active',
};

// ── Small shared pieces ──────────────────────────────────────────────────────
const HelpRow = () => (
  <footer className="ob-help">
    <div className="ob-help-icon">
      <Building size={20} />
    </div>
    <div className="ob-help-text">
      <h4>Need help?</h4>
      <p>Write to our support team at <strong>support@godrim.com</strong></p>
    </div>
    <Button size="sm" variant="outline" className="ob-help-btn">
      Contact support
      <ArrowRight size={14} />
    </Button>
  </footer>
);

const STEPS = [
  {
    title: 'Enter your number',
    text: 'Type the WhatsApp number you want to use, in international format.',
  },
  {
    title: 'Finish in Facebook',
    text: 'Sign in, verify your business and pick your number in Meta’s official signup.',
  },
  {
    title: 'Come back to us',
    text: 'We complete the setup automatically as soon as Meta confirms.',
  },
];

const StepList = ({ current }) => (
  <aside className="ob-side" aria-label="How the setup works">
    <h3 className="ob-side-title">How it works</h3>
    <ol className="ob-steps">
      {STEPS.map((s, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'todo';
        return (
          <li key={s.title} className={`ob-step ob-step--${state}`}>
            <span className="ob-step-marker" aria-hidden="true">
              {state === 'done' ? <Check size={14} strokeWidth={3} /> : i + 1}
            </span>
            <div>
              <strong>{s.title}</strong>
              <p>{s.text}</p>
            </div>
          </li>
        );
      })}
    </ol>
    <div className="ob-side-note">
      <Sparkles size={16} />
      <span>Business verification is handled directly by Meta. We never see your Facebook password.</span>
    </div>
  </aside>
);

const Onboarding = () => {
  const [view, setView] = useState(VIEW.LOADING);
  const [status, setStatus] = useState(null);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);

  const { request } = useApi();
  const { success, error: toastError } = useToast();
  const navigate = useNavigate();

  const loadStatus = useCallback(async (showSpinner = true) => {
    if (showSpinner) setView(VIEW.LOADING);
    try {
      const data = await request('get', '/api/onboarding/status');
      setStatus(data);

      if (data?.status === 'ACTIVE') {
        setView(VIEW.ACTIVE);
      } else if (data?.step === 'AWAITING_META') {
        setView(VIEW.AWAITING);
      } else {
        if (data?.whatsappNumber) setPhone(data.whatsappNumber);
        setView(VIEW.FORM);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load your WhatsApp status. Please refresh the page.');
      setView(VIEW.FORM);
    }
  }, [request]);

  useEffect(() => {
    loadStatus(true);
  }, []);

  useEffect(() => {
    if (view !== VIEW.ACTIVE || status?.senderStatus === 'ONLINE') return undefined;
    const timer = setInterval(() => loadStatus(false), 15000);
    return () => clearInterval(timer);
  }, [view, status?.senderStatus, loadStatus]);

  const handleConnect = async (e) => {
    e.preventDefault();
    setError('');
    setStarting(true);

    try {
      const result = await request('post', '/api/onboarding/start', { phoneNumber: phone });
      if (!result?.signupUrl) {
        throw new Error('missing link');
      }
      window.location.assign(result.signupUrl);
    } catch (err) {
      const msg = err.response?.data?.error
        || err.response?.data?.phoneNumber
        || 'Could not start WhatsApp connection. Please try again.';
      setError(msg);
      toastError(msg);
      setStarting(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (view === VIEW.LOADING) {
    return (
      <div className="ob-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  // ── Connected ──────────────────────────────────────────────────────────────
  if (view === VIEW.ACTIVE) {
    const online = status?.senderStatus === 'ONLINE';

    return (
      <div className="ob-page">
        <header className={`ob-hero ob-hero--status ${online ? 'is-online' : 'is-pending'}`}>
          <div className="ob-status-icon" aria-hidden="true">
            {online ? <CheckCircle size={44} /> : <Clock size={44} />}
          </div>
          <div className="ob-hero-text">
            <h1 className="ob-title">
              {online ? 'WhatsApp is active' : 'Almost there…'}
            </h1>
            <p className="ob-lead">
              {online
                ? 'Your WhatsApp Business number is connected and ready to send messages to your customers.'
                : 'Your number is connected. It can take a few minutes to come fully online. This page updates by itself.'}
            </p>
          </div>
        </header>

        <section className="ob-panel ob-status-panel" aria-live="polite">
          <div className="ob-facts">
            <div className="ob-fact">
              <span className="ob-fact-label"><Smartphone size={16} /> Connected number</span>
              <strong className="ob-fact-value">{status?.whatsappNumber || 'N/A'}</strong>
            </div>
            <div className="ob-fact">
              <span className="ob-fact-label"><Zap size={16} /> Status</span>
              <span className="ob-fact-value">
                <Badge variant={online ? 'success' : 'warning'}>
                  {online ? 'Active' : (status?.senderStatus || 'Connecting')}
                </Badge>
              </span>
            </div>
            <div className="ob-fact">
              <span className="ob-fact-label"><MessageSquare size={16} /> Ready to send</span>
              <span className="ob-fact-value">
                <Badge variant={online ? 'success' : 'warning'}>{online ? 'Yes' : 'Not yet'}</Badge>
              </span>
            </div>
          </div>

          <div className="ob-actions">
            <Button onClick={() => navigate('/send')} className="ob-btn-whatsapp" disabled={!online}>
              <Send size={16} />
              Start sending messages
            </Button>
            {online ? (
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Go to dashboard
              </Button>
            ) : (
              <Button variant="outline" onClick={() => loadStatus(false)}>
                <RefreshCw size={14} />
                Refresh status
              </Button>
            )}
          </div>
        </section>

        <HelpRow />
      </div>
    );
  }

  // ── Form / Awaiting ────────────────────────────────────────────────────────
  const awaiting = view === VIEW.AWAITING;

  return (
    <div className="ob-page">
      <header className="ob-hero">
        <div className="ob-hero-text">
          <span className="ob-chip">
            <Sparkles size={14} />
            Secure signup through Meta
          </span>
          <h1 className="ob-title">Connect your WhatsApp Business number</h1>
          <p className="ob-lead">
            Sign in with Facebook, choose your number, and we finish the setup the moment you return.
          </p>
        </div>

        {/* Decorative preview of what customers will receive */}
        <div className="ob-preview" aria-hidden="true">
          <div className="ob-bubble ob-bubble--in">
            Hello Amina, your order #1042 is on its way.
            <span>10:24</span>
          </div>
          <div className="ob-bubble ob-bubble--out">
            Asante! See you soon.
            <span>10:25</span>
          </div>
          <div className="ob-bubble ob-bubble--in">
            Delivery arrives between 2 and 4 pm.
            <span>10:25</span>
          </div>
        </div>
      </header>

      <div className="ob-body">
        <section className="ob-panel ob-main">
          <div className="ob-panel-head">
            <div className="ob-fb-icon">
              <Globe size={24} />
            </div>
            <div>
              <h2>{awaiting ? 'Waiting for you to finish in Meta' : 'Connect with Facebook'}</h2>
              <p>
                {awaiting
                  ? 'Finish the signup in the Meta window, then check your connection here.'
                  : 'You will verify your business and choose your WhatsApp number inside Facebook’s official signup flow.'}
              </p>
            </div>
          </div>

          {awaiting && (
            <>
              <div className="ob-notice ob-notice--warn">
                <Clock size={18} />
                <p>
                  You started connecting {status?.whatsappNumber ? <b>{status.whatsappNumber}</b> : 'your number'}.
                  If you already finished, check the connection below.
                </p>
              </div>

              <div className="ob-actions ob-actions--stack">
                <Button variant="primary" onClick={() => navigate('/onboarding/callback')} className="ob-btn-facebook">
                  <RefreshCw size={18} />
                  Check my connection
                </Button>
                <Button variant="outline" onClick={() => setView(VIEW.FORM)}>
                  Start again
                </Button>
              </div>
            </>
          )}

          {!awaiting && (
            <form onSubmit={handleConnect} className="ob-form">
              {status?.step === 'EXPIRED' && (
                <div className="ob-notice ob-notice--warn">
                  <AlertCircle size={18} />
                  <p>Your previous attempt expired. Please start again.</p>
                </div>
              )}

              <div className="ob-field">
                <label className="ob-label" htmlFor="ob-phone">WhatsApp number you want to connect</label>
                <Input
                  id="ob-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+255712345678"
                  required
                />
                <p className="ob-hint">Use the number you will select in the next step, in international format.</p>
              </div>

              <div className="ob-checklist">
                <h3>Before you start, have these ready</h3>
                <ul>
                  <li>
                    <CheckCircle size={16} />
                    Admin access to your Facebook Business Portfolio
                  </li>
                  <li>
                    <CheckCircle size={16} />
                    Your business documents for verification
                  </li>
                </ul>
              </div>

              {error && <div className="ob-alert" role="alert">{error}</div>}

              <Button type="submit" variant="primary" loading={starting} className="ob-btn-facebook">
                <Globe size={18} />
                Connect WhatsApp
              </Button>
            </form>
          )}
        </section>

        <StepList current={awaiting ? 1 : 0} />
      </div>

      <HelpRow />
    </div>
  );
};

export default Onboarding;